import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { ChartFormatModel }    from '../typings/chart-format';
import { ChartFormatService }  from '../chart-format.service';
import { ChartInfoPanelModel } from '../typings/chart-info-panel';
import { DonutSegmentModel }   from '../typings/donut-segment';
import { infoPanelPosition }   from '../chart-info-panel/chart-info-panel-position';

// ring radius in SVG user units
const RADIUS : number = 70;

interface RenderedSegment {
  color       : string;
  dashArray   : string;
  dashOffset  : number;
  label       : string;
  percent     : number;
  percentLabel: string;
  segment     : DonutSegmentModel;
  value       : number;
}

// A share-of-total ring with a centre headline; the Other slice opens into a ring of its entries
@Component({
  selector   : 'wor-donut-chart',
  styles: [ require('./donut-chart.component.scss') ],
  template: require('./donut-chart.component.html')
})
export class DonutChartComponent implements OnChanges {
  @Input() centerLabel : string                   = '';
  @Input() format      : ChartFormatModel         = 'money';
  @Input() segments    : Array<DonutSegmentModel> = [];

  @Output() segmentPressed : EventEmitter<DonutSegmentModel> = new EventEmitter<DonutSegmentModel>();

  public circumference : number                 = 2 * Math.PI * RADIUS;
  public hoverIndex    : number                 = -1;
  public infoPanel     : ChartInfoPanelModel    = { isVisible: false, rows: [], title: '', x: 0, y: 0 };
  public isExpanded    : boolean                = false;
  public radius        : number                 = RADIUS;
  public rendered      : Array<RenderedSegment> = [];
  public total         : number                 = 0;

  constructor (
    private chartFormatService: ChartFormatService
  ) {}

  ngOnChanges () : void {
    this.isExpanded = false;
    this.render();
  }

  collapse () : void {
    this.isExpanded = false;
    this.render();
  }

  formatCenter ( value : number ) : string {
    return this.chartFormatService.headline(value, this.format);
  }

  formatValue ( value : number ) : string {
    return this.chartFormatService.value(value, this.format);
  }

  hideInfoPanel () : void {
    this.hoverIndex = -1;
    this.infoPanel = { ...this.infoPanel, isVisible: false };
  }

  isDrillable ( rendered : RenderedSegment ) : boolean {
    return this.isExpandable(rendered.segment)
      || ( !!rendered.segment.isDrillable && this.segmentPressed.observed );
  }

  onMove ( event : MouseEvent, segment : RenderedSegment, index : number ) : void {
    const wrapper = ( event.currentTarget as Element ).closest('.chart-wrapper') as HTMLElement;
    const bounds = wrapper.getBoundingClientRect();

    this.hoverIndex = index;

    this.infoPanel = {
      isVisible: true,
      rows     : [{
        color: segment.color,
        label: segment.label,
        prior: segment.percentLabel,
        value: this.formatValue(segment.value)
      }],
      title: segment.label,
      ...infoPanelPosition(event, bounds)
    };
  }

  press ( rendered : RenderedSegment ) : void {
    if ( this.isExpandable(rendered.segment) ) {
      this.isExpanded = true;
      this.render();

      return;
    }

    if ( !!rendered.segment.isDrillable && this.segmentPressed.observed ) {
      this.segmentPressed.emit(rendered.segment);
    }
  }

  trackByIndex ( index : number ) : number {
    return index;
  }

  private isExpandable ( segment : DonutSegmentModel ) : boolean {
    return !this.isExpanded && !!segment.others && segment.others.length > 0;
  }

  private render () : void {
    const other = this.segments.find(segment => segment.others && segment.others.length > 0);
    const source = this.isExpanded && other
      ? other.others
      : this.segments;

    this.hideInfoPanel();
    this.rendered = [];
    this.total = source.reduce(( sum, segment ) => sum + Math.max(segment.value, 0), 0);

    if ( this.total <= 0 ) {
      return;
    }

    let offset = 0;

    this.rendered = source
      .filter(segment => segment.value > 0)
      .map(segment => {
        const percent = ( segment.value / this.total ) * 100;
        const length = ( segment.value / this.total ) * this.circumference;
        const item = {
          color       : segment.color,
          dashArray   : `${ Math.max(length - 2, 0) } ${ this.circumference - Math.max(length - 2, 0) }`,
          dashOffset  : -offset,
          label       : segment.label,
          percent,
          percentLabel: `${ percent.toFixed(1) }%`,
          segment,
          value       : segment.value
        };

        offset += length;

        return item;
      });
  }
}
